import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import WelcomeInfoModal from '../components/modal/WelcomeInfoModal';
import nepalMap from '../assets/img/nepal-map.svg';

// Landing pitches a PRIVATE, verification-gated wholesale marketplace —
// no catalog, no prices, and no invented metrics before launch.

const MISSION = [
  {
    kicker: '01',
    title: 'One trusted marketplace',
    desc: "Nepal's B2B trade, moved onto a single platform where every transaction is secure and every counterparty is accountable.",
  },
  {
    kicker: '02',
    title: 'Verified businesses only',
    desc: 'Access is granted after your business registration and legal documents are checked — everyone you trade with has been through the same gate.',
  },
  {
    kicker: '03',
    title: 'Built for bulk',
    desc: 'Minimum order quantities, wholesale price tiers, and streamlined procurement — designed around how bulk trade actually works.',
  },
];

const VERIFICATION_STEPS = [
  {
    step: '1',
    title: 'Submit documents',
    desc: 'Provide your business registration and legal documents',
  },
  {
    step: '2',
    title: 'Verification check',
    desc: 'We verify your business credentials for authenticity',
  },
  {
    step: '3',
    title: 'Access the platform',
    desc: 'Start trading with verified suppliers and buyers',
  },
];

const CATEGORIES = [
  'Accessories',
  'Footwear',
  "Kids' Clothing",
  "Men's Clothing",
  "Women's Clothing",
];

// Nepal dot-matrix map, tinted via CSS mask so it renders in brand emerald.
const NepalMap = ({ className = '' }) => (
  <div
    aria-hidden="true"
    className={`aspect-[2/1] ${className}`}
    style={{
      WebkitMaskImage: `url(${nepalMap})`,
      maskImage: `url(${nepalMap})`,
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat',
      WebkitMaskSize: 'contain',
      maskSize: 'contain',
      WebkitMaskPosition: 'center',
      maskPosition: 'center',
    }}
  >
    <div className="w-full h-full bg-gradient-to-tr from-brand-200 via-brand-400 to-brand-600" />
  </div>
);

const Landing = () => {
  const { user } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (user) {
      navigate('/home');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!user) {
      const timer = setTimeout(() => setShowWelcomeModal(true), 400);
      return () => clearTimeout(timer);
    }
  }, [user]);

  if (user) {
    return null;
  }

  const scrollToHowItWorks = () =>
    document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="min-h-screen bg-gray-50">
      <WelcomeInfoModal
        isOpen={showWelcomeModal}
        onClose={() => setShowWelcomeModal(false)}
      />

      {/* Hero */}
      <header className="relative max-w-7xl mx-auto overflow-hidden">
        {/* Desktop */}
        <div className="hidden md:flex items-center py-24 px-12 lg:px-20 min-h-[80vh]">
          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3/5 flex items-center justify-end pr-8 opacity-90">
            <NepalMap className="w-full max-w-2xl lg:max-w-3xl" />
          </div>
          <div className="relative z-10 w-full max-w-2xl text-left">
            <p className="text-sm font-semibold tracking-widest uppercase text-brand-700 mb-4">
              Nepal's private B2B marketplace
            </p>
            <h1 className="text-5xl lg:text-6xl font-normal italic mb-2">
              Streamlined Sales,
            </h1>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6 italic">
              Quick Delivery
            </h1>
            <p className="text-lg lg:text-xl mb-12 max-w-xl leading-relaxed text-gray-700">
              Connecting verified sellers &amp; buyers for faster, safer bulk
              trade — all in one private platform.
            </p>
            <div className="flex justify-start gap-6">
              <button
                onClick={() => navigate('/register')}
                className="px-10 py-4 bg-brand-600 hover:bg-brand-700 text-white font-bold rounded-lg transition-colors text-lg"
              >
                Register Now
              </button>
              <button
                onClick={scrollToHowItWorks}
                className="px-10 py-4 rounded-lg bg-white border border-gray-300 text-gray-800 hover:border-brand-600 hover:text-brand-700 transition text-lg font-medium"
              >
                How it works
              </button>
            </div>
          </div>
        </div>

        {/* Mobile */}
        <div className="flex md:hidden flex-col items-center py-16 px-4 text-center">
          <p className="text-xs font-semibold tracking-widest uppercase text-brand-700 mb-3">
            Nepal's private B2B marketplace
          </p>
          <div className="mb-6">
            <h1 className="text-3xl font-normal italic">Streamlined Sales,</h1>
            <h1 className="text-3xl font-bold mb-4 italic">Quick Delivery</h1>
            <p className="text-sm sm:text-base mb-0 max-w-sm mx-auto leading-relaxed text-gray-700">
              Connecting verified sellers &amp; buyers for faster, safer bulk
              trade — all in one private platform.
            </p>
          </div>
          <NepalMap className="w-full mb-6 max-w-sm" />
          <div className="flex flex-col gap-3 w-full max-w-xs">
            <button
              onClick={() => navigate('/register')}
              className="px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white font-bold rounded-lg transition-colors text-sm w-full"
            >
              Register Now
            </button>
            <button
              onClick={scrollToHowItWorks}
              className="px-6 py-3 rounded-lg bg-white border border-gray-300 text-gray-800 hover:border-brand-600 hover:text-brand-700 transition text-sm font-medium w-full"
            >
              How it works
            </button>
          </div>
        </div>
      </header>

      {/* Mission — editorial columns, no cards, no numbers */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold max-w-md mb-14">
            Why Wholesale<span className="text-brand-600">Dai</span> exists
          </h2>
          <div className="grid md:grid-cols-3 gap-10 md:gap-8">
            {MISSION.map((item) => (
              <div key={item.kicker} className="border-t-2 border-brand-600 pt-6">
                <p className="text-sm font-semibold text-brand-700 mb-2">
                  {item.kicker}
                </p>
                <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Verification process */}
      <section id="how-it-works" className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-16">
            Secure Business Verification Process
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {VERIFICATION_STEPS.map((feature) => (
              <div
                key={feature.step}
                className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 text-left"
              >
                <div className="w-10 h-10 rounded-full bg-brand-100 text-brand-700 font-bold flex items-center justify-center mb-5">
                  {feature.step}
                </div>
                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="bg-white py-16 px-4">
        <div className="max-w-5xl mx-auto md:flex items-start gap-16">
          <div className="md:w-2/5 mb-8 md:mb-0">
            <h2 className="text-3xl font-bold mb-4">Wholesale Categories</h2>
            <p className="text-gray-600">
              Suppliers list across the categories below — your account is
              assigned the ones relevant to your business.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center text-brand-600 hover:text-brand-700 font-medium transition-colors mt-4"
            >
              View the full catalog after registration
              <svg className="ml-2 w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </Link>
          </div>
          <div className="md:w-3/5 flex flex-wrap gap-3 content-start">
            {CATEGORIES.map((name) => (
              <span
                key={name}
                className="px-5 py-2.5 rounded-full border border-gray-200 bg-gray-50 text-gray-800 font-medium"
              >
                {name}
              </span>
            ))}
            <span className="px-5 py-2.5 rounded-full border border-dashed border-brand-300 bg-brand-50 text-brand-700 font-medium">
              + more after launch
            </span>
          </div>
        </div>
      </section>

      {/* Why choose + CTA */}
      <section className="py-20 px-4 bg-brand-50">
        <div className="max-w-6xl mx-auto">
          <div className="md:flex gap-12 items-center">
            <div className="md:w-1/2">
              <h2 className="text-3xl font-bold mb-6">
                Why Choose WholesaleDai?
              </h2>
              <ul className="space-y-4">
                {[
                  'Verified suppliers and buyers only',
                  'Secure and transparent transactions',
                  'Competitive wholesale pricing',
                  'Streamlined procurement process',
                  'Dedicated business support',
                  'Nepal-focused B2B platform',
                ].map((benefit, index) => (
                  <li key={index} className="flex items-center">
                    <div className="w-6 h-6 bg-brand-600 text-white rounded-full flex items-center justify-center mr-3">
                      ✓
                    </div>
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
            <div className="md:w-1/2 mt-10 md:mt-0">
              <div className="bg-brand-600 rounded-2xl p-10 text-white">
                <h3 className="text-2xl font-bold mb-3">Let's Grow Together!</h3>
                <p className="mb-8 text-brand-100 leading-relaxed">
                  Join Nepal's B2B marketplace and transform the way you do
                  business.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/register"
                    className="!no-underline px-6 py-3 bg-white !text-brand-700 hover:bg-brand-50 font-semibold rounded-lg transition-colors text-center"
                  >
                    Start Business Verification
                  </Link>
                  <Link
                    to="/login"
                    className="!no-underline px-6 py-3 border-2 border-white/70 !text-white hover:bg-brand-700 font-medium rounded-lg transition text-center"
                  >
                    Existing Member Login
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Landing;
